import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePostHog } from '@posthog/react'
import ContentHeader from '../components/ContentHeader'
import { createClient } from '../api/crm'
import { getSessionAuth } from '../utils/sessionAuth'
import { ensureSessionUserId, getSessionUserId } from '../utils/sessionUser'
import '../style/views/ContactCreateView.css'

function ContactCreateView() {
  const posthog = usePostHog()
  const navigate = useNavigate()
  const [userId, setUserId] = useState<string | null>(() => getSessionUserId())
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    cell: '',
    company: '',
    summary: '',
  })

  const handleChange = (key: keyof typeof form, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)
      const ensuredId = userId || (await ensureSessionUserId(getSessionAuth()))
      if (!ensuredId) throw new Error('Missing user id')
      if (!userId) setUserId(ensuredId)
      await createClient({
        user_id: ensuredId,
        conversation_id: null,
        client: {
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email,
          cell: form.cell,
          company: form.company,
          notes: form.summary,
        },
      })
      posthog?.capture('contact_created', {
        has_email: Boolean(form.email),
        has_phone: Boolean(form.cell),
        has_company: Boolean(form.company),
      })
      navigate('/contacts')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to create contact.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <ContentHeader title="New Contact" />
      <div className="content-panel">
        <div className="card-panel">
          <p className="eyebrow">Details</p>
          <h1 style={{ marginTop: '2px' }}>Add contact</h1>
          <div className="form-grid" style={{ marginTop: '12px' }}>
            <label>
              <span>First name</span>
              <input value={form.first_name} onChange={(e) => handleChange('first_name', e.target.value)} />
            </label>
            <label>
              <span>Last name</span>
              <input value={form.last_name} onChange={(e) => handleChange('last_name', e.target.value)} />
            </label>
            <label className="full-row">
              <span>Email</span>
              <input value={form.email} onChange={(e) => handleChange('email', e.target.value)} type="email" />
            </label>
            <label className="full-row">
              <span>Phone number</span>
              <input value={form.cell} onChange={(e) => handleChange('cell', e.target.value)} />
            </label>
            <label className="full-row">
              <span>Company</span>
              <input value={form.company} onChange={(e) => handleChange('company', e.target.value)} />
            </label>
            <label className="full-row">
              <span>Notes</span>
              <textarea
                value={form.summary}
                onChange={(e) => handleChange('summary', e.target.value)}
                rows={6}
                className="edit-input"
              />
            </label>
          </div>
          {error && (
            <div className="muted" style={{ color: '#b42318', marginTop: '8px' }}>
              {error}
            </div>
          )}
          <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
            <button type="button" className="ghost-button" onClick={() => navigate('/contacts')} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="primary-button" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default ContactCreateView
